import { create } from 'zustand'
import { getJson } from '../utils/http'
import { calcMaxNumberOfChildren } from '../components/search-bar/utils'
import type { FilterData } from '../components/jpe-top-page/search-elements/FilterModal'
import type { NurseryItemData, CheckInMonthData } from '../components/jpe-top-page/nureseries/type'
import type { FilterSection } from '../components/jpe-top-page/nureseries/SortFilterBar'
import { handleGetTheNearestMonth, buildFilterQueryParams } from '../components/jpe-top-page/utils'

export type SortOption = 'recommended' | 'lowest_price' | 'near_airport'

export const ITEMS_PER_PAGE = 9

export type ChildrenType = {
  id: number
  name: string
  value: string
}

export type Birthdate = {
  day: string
  year: string
  month: string
}

export type DataSearchType = {
  number_of_adults: number
  number_of_children: number
  accept_class: ChildrenType[]
  stay_day: string
  plan_month: string[]
  text_search: string
  number_of_preschoolers_1: number // Not in Program (Aged 0-6)
  number_of_students_1: number // Not in Program (Aged 7-12)
  birthdays: Birthdate[] // Array of child birthdates
}

export const DATA_SEARCH_INIT: DataSearchType = {
  number_of_adults: 2,
  number_of_children: 1,
  accept_class: [],
  stay_day: '',
  plan_month: [],
  text_search: '',
  number_of_preschoolers_1: 0,
  number_of_students_1: 0,
  birthdays: [{ day: '', year: '', month: '' }],
}

interface SearchStore {
  // Search data state
  searchData: DataSearchType

  // Modal state
  isSearchModalOpen: boolean
  isFilterModalOpen: boolean

  // Filter data state
  filterData: FilterData | null

  // Sort state
  selectedSort: SortOption

  // Currency state
  selectedCurrency: string
  isCurrencyPopoverOpen: boolean

  // Home menu state
  isHomeMenuPopoverOpen: boolean

  // Nursery data state
  nurseries: NurseryItemData[]
  totalCount: number
  resultCount: number
  loading: boolean
  aliases: string[]

  // Selection state
  selectedNurseryId: number | null
  selectedNursery: NurseryItemData | null
  selectedPinPosition: { x: number; y: number } | null
  selectedMarkerRef: HTMLDivElement | null
  mapContainerRef: HTMLDivElement | null

  // UI state
  isDesktop: boolean
  scrollToSection: FilterSection | undefined
  shouldStartCollapsed: boolean

  // Pagination state
  currentPage: number

  // CheckInMonths state
  checkInMonthsData: Record<string, CheckInMonthData[]>
  checkInMonthsLoading: Set<string>
  checkInMonthsError: Set<string>

  // Actions
  setSearchData: (data: DataSearchType) => void
  updateSearchField: <K extends keyof DataSearchType>(key: K, value: DataSearchType[K]) => void
  clearSearchData: () => void
  setModalOpen: (isOpen: boolean) => void
  setFilterModalOpen: (isOpen: boolean) => void

  // Filter actions
  setFilterData: (data: FilterData) => void
  clearFilterData: () => void
  removeFilter: (filterType: string, value: string) => void
  syncFiltersToUrl: (filterDataOverride?: FilterData | null) => void
  loadFiltersFromUrl: () => void

  // Sort actions
  setSelectedSort: (sort: SortOption) => void
  loadSortFromUrl: () => void

  // Currency actions
  setSelectedCurrency: (currency: string) => void
  setCurrencyPopoverOpen: (isOpen: boolean) => void

  // Home menu actions
  setHomeMenuPopoverOpen: (isOpen: boolean) => void

  // Nursery data actions
  setNurseries: (nurseries: NurseryItemData[], totalCount: number, resultCount: number) => void
  setLoading: (loading: boolean) => void
  setAliases: (aliases: string[]) => void

  // Selection actions
  setSelectedNurseryId: (id: number | null) => void
  setSelectedNursery: (nursery: NurseryItemData | null) => void
  setSelectedPinPosition: (position: { x: number; y: number } | null) => void
  setSelectedMarkerRef: (ref: HTMLDivElement | null) => void
  setMapContainerRef: (ref: HTMLDivElement | null) => void
  clearSelection: () => void

  // UI actions
  setIsDesktop: (isDesktop: boolean) => void
  setScrollToSection: (section?: FilterSection) => void
  setShouldStartCollapsed: (value: boolean) => void

  // Pagination actions
  setCurrentPage: (page: number) => void
  loadPageFromUrl: () => void
  syncPageToUrl: () => void

  // URL sync utilities
  loadFromUrl: () => void
  applySearchAndUpdateUrl: () => void
  generateShareUrl: () => string

  // CheckInMonths actions
  fetchCheckInMonths: (nurseryIds: string[]) => Promise<void>
  clearCheckInMonthsCache: () => void
  retryCheckInMonths: (nurseryIds: string[]) => Promise<void>
}

export const useSearchStore = create<SearchStore>()((set, get) => ({
  searchData: DATA_SEARCH_INIT,
  isSearchModalOpen: false,
  isFilterModalOpen: false,
  filterData: null,
  selectedSort: 'recommended',
  selectedCurrency: (typeof window !== 'undefined' && localStorage.getItem('selectedCurrency')) || 'JPY',
  isCurrencyPopoverOpen: false,
  isHomeMenuPopoverOpen: false,

  // Nursery data state
  nurseries: [],
  totalCount: 0,
  resultCount: 0,
  loading: true,
  aliases: [],

  // Selection state
  selectedNurseryId: null,
  selectedNursery: null,
  selectedPinPosition: null,
  selectedMarkerRef: null,
  mapContainerRef: null,

  // UI state
  isDesktop: typeof window !== 'undefined' && window.innerWidth >= 768,
  scrollToSection: undefined,
  shouldStartCollapsed: false,

  // Pagination state
  currentPage: 1,

  // CheckInMonths state
  checkInMonthsData: {},
  checkInMonthsLoading: new Set<string>(),
  checkInMonthsError: new Set<string>(),

  setSearchData: (data: DataSearchType) => {
    set({ searchData: data })
  },

  updateSearchField: (key, value) => {
    set((state) => ({
      searchData: {
        ...state.searchData,
        [key]: value,
      },
    }))
  },

  clearSearchData: () => {
    set({ searchData: DATA_SEARCH_INIT })
  },

  setModalOpen: (isOpen: boolean) => {
    set({ isSearchModalOpen: isOpen })
  },

  setFilterModalOpen: (isOpen: boolean) => {
    set({ isFilterModalOpen: isOpen })
  },

  loadFromUrl: () => {
    const queryString = window.location.search
    const queryParams = new URLSearchParams(queryString)

    const tempObject: DataSearchType = { ...DATA_SEARCH_INIT }

    const arrayKeys = ['plan_month']
    const stringKeys = ['stay_day', 'text_search']
    const numberKeys = ['number_of_adults', 'number_of_preschoolers_1', 'number_of_students_1']
    const keySearch = [
      'number_of_adults',
      'maximum_number_of_adults',
      'accept_class',
      'stay_day',
      'plan_month',
      'text_search',
      'number_of_preschoolers_1',
      'number_of_students_1',
      'birthdays',
    ]

    // Map domestic param names to internal names
    const paramMapping: Record<string, string> = {
      maximum_number_of_adults: 'number_of_adults',
    }

    queryParams.forEach((value, key) => {
      // Map domestic param names to internal names
      const mappedKey = paramMapping[key] || key
      if (keySearch.includes(key)) {
        if (arrayKeys.includes(mappedKey)) {
          if (mappedKey === 'plan_month' && value === 'recently') {
            const nearestMonths = handleGetTheNearestMonth()
            tempObject[mappedKey] = nearestMonths
          } else {
            tempObject[mappedKey] = value ? value.split(',') : []
          }
        } else if (mappedKey === 'accept_class') {
          const dataArray = value ? value.split(',') : []
          tempObject[mappedKey] = dataArray.map((item, index) => ({
            id: index + 1,
            name: item,
            value: item,
          }))
        } else if (mappedKey === 'birthdays') {
          const dataArray = value ? value.split(',') : []
          tempObject[mappedKey] = dataArray.map((item) => {
            const [year, month, day] = item.split('-')
            return { year: year || '', month: month || '', day: day || '' }
          })
        } else if (numberKeys.includes(mappedKey)) {
          tempObject[mappedKey] = Number(value)
        } else if (stringKeys.includes(mappedKey)) {
          tempObject[mappedKey] = value
        } else {
          tempObject[mappedKey] = value
        }
      }
    })

    // number_of_children は birthdays の件数から算出（URLの maximum_number_of_children は合計値のため直接使えない）
    tempObject.number_of_children = tempObject.birthdays.length

    // 子どもは最低1人必要
    if (tempObject.number_of_children < 1) {
      tempObject.number_of_children = 1
      if (tempObject.birthdays.length < 1) {
        tempObject.birthdays = [{ day: '', year: '', month: '' }]
      }
    }

    // 大人はデフォルト2人（最低1人必要）
    if (tempObject.number_of_adults < 1) {
      tempObject.number_of_adults = DATA_SEARCH_INIT.number_of_adults
    }

    set({ searchData: tempObject })
  },

  applySearchAndUpdateUrl: () => {
    const { searchData, filterData, selectedSort } = get()
    // Format birthdays: [{year: '2020', month: '01', day: '01'}] -> "2020-01-01,2021-05-01"
    const validBirthdays = searchData.birthdays.filter((bd) => bd.year && bd.month && bd.day)
    const birthdaysString = validBirthdays.map((bd) => `${bd.year}-${parseInt(bd.month)}-${parseInt(bd.day)}`).join(',')

    const numberOfStudyAbroad = validBirthdays.length
    const maxChildren = calcMaxNumberOfChildren(
      numberOfStudyAbroad,
      searchData.number_of_preschoolers_1,
      searchData.number_of_students_1,
    )

    const query = {
      maximum_number_of_adults: String(searchData.number_of_adults),
      maximum_number_of_children: String(maxChildren),
      accept_class: searchData.accept_class?.map((item) => item.value).join(','),
      stay_day: searchData.stay_day,
      plan_month: searchData.plan_month?.join(','),
      text_search: searchData.text_search,
      number_of_preschoolers_1: String(searchData.number_of_preschoolers_1),
      number_of_students_1: String(searchData.number_of_students_1),
      number_of_study_abroad: String(numberOfStudyAbroad),
      birthdays: birthdaysString,
    }

    const currentQueryParams = new URLSearchParams(window.location.search)

    // Apply sort param
    if (selectedSort === 'recommended') {
      currentQueryParams.delete('sort')
    } else {
      currentQueryParams.set('sort', selectedSort)
    }

    // Apply search params
    Object.keys(query).forEach((key) => {
      if (query[key] && !['0', 'false', ''].includes(query[key])) {
        currentQueryParams.set(key, query[key])
      } else {
        currentQueryParams.delete(key)
      }
    })

    // Apply filter params using shared helper
    buildFilterQueryParams(filterData, currentQueryParams)

    // Reset page to 1 when search/filters change
    set({ currentPage: 1 })
    currentQueryParams.delete('page')

    const queryURL = currentQueryParams.toString()
    const newUrl = queryURL ? `?${queryURL}` : window.location.pathname

    window.history.pushState({}, '', newUrl)
  },

  // Filter data actions
  setFilterData: (data: FilterData) => {
    set({ filterData: data })
  },

  clearFilterData: () => {
    set({ filterData: null })
  },

  removeFilter: (filterType: string, value: string) => {
    const { filterData } = get()
    if (!filterData) return

    const newFilterData = { ...filterData }

    switch (filterType) {
      case 'mobilityConditions':
        newFilterData.mobilityConditions = newFilterData.mobilityConditions.filter((v) => v !== value)
        break
      case 'mealService':
        newFilterData.mealService = []
        break
      case 'nurseryFeatures':
        newFilterData.nurseryFeatures = newFilterData.nurseryFeatures.filter((v) => v !== value)
        break
      case 'roomType':
        newFilterData.roomType = newFilterData.roomType.filter((v) => v !== value)
        break
      case 'kitchen':
        newFilterData.kitchen = []
        break
      case 'bathroom':
        newFilterData.bathroom = []
        break
      case 'searchText':
        newFilterData.searchText = ''
        break
    }

    set({ filterData: newFilterData })
    // Re-sync to URL with the new filterData (not the stale store data)
    get().syncFiltersToUrl(newFilterData)
  },

  syncFiltersToUrl: (filterDataOverride?: FilterData | null) => {
    const { filterData: storeFilterData, selectedSort } = get()
    const filterData = filterDataOverride !== undefined ? filterDataOverride : storeFilterData
    if (!filterData) {
      return
    }

    const currentQueryParams = new URLSearchParams(window.location.search)

    // Sync sort parameter
    if (selectedSort === 'recommended') {
      currentQueryParams.delete('sort')
    } else {
      currentQueryParams.set('sort', selectedSort)
    }

    // Apply filter params using shared helper
    buildFilterQueryParams(filterData, currentQueryParams)

    const queryURL = currentQueryParams.toString()
    const newUrl = queryURL ? `?${queryURL}` : window.location.pathname

    window.history.replaceState({}, '', newUrl)
  },

  loadFiltersFromUrl: () => {
    const queryParams = new URLSearchParams(window.location.search)

    // Check if there are any filter params
    const hasMobility = queryParams.has('room_environment')
    const hasMeals = queryParams.has('allergies_en')
    const hasFeatures = queryParams.has('feature')
    const hasAccommodation = queryParams.has('room_type_en')
    const hasKitchen = queryParams.has('kitchen_en')
    const hasBathroom = queryParams.has('bathroom_en')
    const hasSearch = queryParams.has('text_search')

    if (!hasMobility && !hasMeals && !hasFeatures && !hasAccommodation && !hasKitchen && !hasBathroom && !hasSearch) {
      return
    }

    const filterData: FilterData = {
      mobilityConditions: queryParams.get('room_environment')?.split(',').filter(Boolean) || [],
      mealService: queryParams.get('allergies_en')?.split(',').filter(Boolean) || [],
      nurseryFeatures: queryParams.get('feature')?.split(',').filter(Boolean) || [],
      roomType: queryParams.get('room_type_en')?.split(',').filter(Boolean) || [],
      kitchen: queryParams.get('kitchen_en')?.split(',').filter(Boolean) || [],
      bathroom: queryParams.get('bathroom_en')?.split(',').filter(Boolean) || [],
      searchText: queryParams.get('text_search') || '',
    }

    set({ filterData })
  },

  // Sort actions
  setSelectedSort: (sort: SortOption) => {
    set({ selectedSort: sort })

    // Update URL param
    const currentQueryParams = new URLSearchParams(window.location.search)
    if (sort === 'recommended') {
      // Remove sort param if it's the default
      currentQueryParams.delete('sort')
    } else {
      currentQueryParams.set('sort', sort)
    }

    const queryURL = currentQueryParams.toString()
    const newUrl = queryURL ? `?${queryURL}` : window.location.pathname
    window.history.replaceState({}, '', newUrl)
  },

  loadSortFromUrl: () => {
    if (typeof window === 'undefined') return

    const params = new URLSearchParams(window.location.search)
    const sortParam = params.get('sort')

    if (sortParam && ['recommended', 'lowest_price', 'near_airport'].includes(sortParam)) {
      set({ selectedSort: sortParam as SortOption })
    }
  },

  // Currency actions
  setSelectedCurrency: (currency: string) => {
    localStorage.setItem('selectedCurrency', currency)
    set({ selectedCurrency: currency })
  },

  setCurrencyPopoverOpen: (isOpen: boolean) => {
    set({ isCurrencyPopoverOpen: isOpen })
  },

  // Home menu actions
  setHomeMenuPopoverOpen: (isOpen: boolean) => {
    set({ isHomeMenuPopoverOpen: isOpen })
  },

  // Nursery data actions
  setNurseries: (nurseries: NurseryItemData[], totalCount: number, resultCount: number) => {
    set({ nurseries, totalCount, resultCount })
  },

  setLoading: (loading: boolean) => {
    set({ loading })
  },

  setAliases: (aliases: string[]) => {
    set({ aliases })
  },

  // Selection actions
  setSelectedNurseryId: (id: number | null) => {
    set({ selectedNurseryId: id })
  },

  setSelectedNursery: (nursery: NurseryItemData | null) => {
    set({ selectedNursery: nursery })
  },

  setSelectedPinPosition: (position: { x: number; y: number } | null) => {
    set({ selectedPinPosition: position })
  },

  setSelectedMarkerRef: (ref: HTMLDivElement | null) => {
    set({ selectedMarkerRef: ref })
  },

  setMapContainerRef: (ref: HTMLDivElement | null) => {
    set({ mapContainerRef: ref })
  },

  clearSelection: () => {
    set({
      selectedNurseryId: null,
      selectedNursery: null,
      selectedPinPosition: null,
      selectedMarkerRef: null,
      shouldStartCollapsed: true,
    })
  },

  // UI actions
  setIsDesktop: (isDesktop: boolean) => {
    set({ isDesktop })
  },

  setScrollToSection: (section?: FilterSection) => {
    set({ scrollToSection: section })
  },

  setShouldStartCollapsed: (value: boolean) => {
    set({ shouldStartCollapsed: value })
  },

  // Pagination actions
  setCurrentPage: (page: number) => {
    set({ currentPage: page })
    get().syncPageToUrl()
  },

  loadPageFromUrl: () => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const pageParam = params.get('page')
    if (pageParam) {
      const page = parseInt(pageParam, 10)
      if (!isNaN(page) && page > 0) {
        set({ currentPage: page })
      }
    }
  },

  syncPageToUrl: () => {
    const { currentPage } = get()
    const params = new URLSearchParams(window.location.search)

    if (currentPage === 1) {
      params.delete('page')
    } else {
      params.set('page', String(currentPage))
    }

    const queryURL = params.toString()
    const newUrl = queryURL ? `?${queryURL}` : window.location.pathname
    window.history.replaceState({}, '', newUrl)
  },

  generateShareUrl: () => {
    const { searchData, filterData, selectedSort } = get()

    // Get clean base URL (origin + pathname, no existing params)
    const baseUrl = typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : ''

    // Build query params from current store state
    const params = new URLSearchParams()

    // Add search data params
    if (searchData.number_of_adults > 0) {
      params.set('maximum_number_of_adults', String(searchData.number_of_adults))
    }
    if (searchData.accept_class?.length > 0) {
      params.set('accept_class', searchData.accept_class.map((item) => item.value).join(','))
    }
    if (searchData.stay_day) {
      params.set('stay_day', searchData.stay_day)
    }
    if (searchData.plan_month?.length > 0) {
      params.set('plan_month', searchData.plan_month.join(','))
    }
    if (searchData.text_search) {
      params.set('text_search', searchData.text_search)
    }
    if (searchData.number_of_preschoolers_1 > 0) {
      params.set('number_of_preschoolers_1', String(searchData.number_of_preschoolers_1))
    }
    if (searchData.number_of_students_1 > 0) {
      params.set('number_of_students_1', String(searchData.number_of_students_1))
    }
    let numberOfStudyAbroad = 0
    if (searchData.birthdays?.length > 0) {
      const validBirthdays = searchData.birthdays.filter((bd) => bd.year && bd.month && bd.day)
      const birthdaysString = validBirthdays.map((bd) => `${bd.year}-${parseInt(bd.month)}-${parseInt(bd.day)}`).join(',')
      numberOfStudyAbroad = validBirthdays.length
      if (birthdaysString) {
        params.set('birthdays', birthdaysString)
        params.set('number_of_study_abroad', String(numberOfStudyAbroad))
      }
    }
    // maximum_number_of_children = number_of_study_abroad + number_of_preschoolers_1 + number_of_students_1
    const maxChildren = calcMaxNumberOfChildren(
      numberOfStudyAbroad,
      searchData.number_of_preschoolers_1,
      searchData.number_of_students_1,
    )
    if (maxChildren > 0) {
      params.set('maximum_number_of_children', String(maxChildren))
    }

    // Add sort param (only if not default)
    if (selectedSort !== 'recommended') {
      params.set('sort', selectedSort)
    }

    // Add filter params using shared helper
    buildFilterQueryParams(filterData, params)

    // Build final URL
    const queryString = params.toString()
    return queryString ? `${baseUrl}?${queryString}` : baseUrl
  },

  // CheckInMonths actions
  fetchCheckInMonths: async (nurseryIds: string[]) => {
    const { checkInMonthsData, checkInMonthsLoading } = get()

    // Filter out nurseryIds that already have data or are currently loading
    const idsToFetch = nurseryIds.filter((id) => !checkInMonthsData[id] && !checkInMonthsLoading.has(id))

    if (idsToFetch.length === 0) return

    // Mark these IDs as loading
    const newLoading = new Set(checkInMonthsLoading)
    idsToFetch.forEach((id) => newLoading.add(id))

    // Clear any previous errors for these IDs
    const newError = new Set(get().checkInMonthsError)
    idsToFetch.forEach((id) => newError.delete(id))

    set({ checkInMonthsLoading: newLoading, checkInMonthsError: newError })

    try {
      const items = await getJson('/nurseries/check_in_months.json', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        params: {
          jpe: true,
          nursery_ids: idsToFetch.join(','),
        },
      })

      // Process response and update store
      // API returns kintoneId as number and month as number, convert to match frontend types
      const newData = { ...get().checkInMonthsData }
      items.forEach(
        (item: { kintoneId: number; checkInMonths: Array<{ month: number; percent: number | null; price: number | null }> }) => {
          const key = String(item.kintoneId)
          newData[key] = item.checkInMonths.map((m) => ({
            month: String(m.month),
            percent: m.percent,
            price: m.price,
          }))
        },
      )

      // Remove from loading set
      const updatedLoading = new Set(get().checkInMonthsLoading)
      idsToFetch.forEach((id) => updatedLoading.delete(id))

      set({ checkInMonthsData: newData, checkInMonthsLoading: updatedLoading })
    } catch {
      // Mark these IDs as errored
      const updatedError = new Set(get().checkInMonthsError)
      idsToFetch.forEach((id) => updatedError.add(id))

      // Remove from loading set
      const updatedLoading = new Set(get().checkInMonthsLoading)
      idsToFetch.forEach((id) => updatedLoading.delete(id))

      set({ checkInMonthsLoading: updatedLoading, checkInMonthsError: updatedError })
    }
  },

  clearCheckInMonthsCache: () => {
    set({
      checkInMonthsData: {},
      checkInMonthsLoading: new Set<string>(),
      checkInMonthsError: new Set<string>(),
    })
  },

  retryCheckInMonths: async (nurseryIds: string[]) => {
    // Clear error state for these IDs first
    const newError = new Set(get().checkInMonthsError)
    nurseryIds.forEach((id) => newError.delete(id))
    set({ checkInMonthsError: newError })

    // Then fetch
    await get().fetchCheckInMonths(nurseryIds)
  },
}))
